import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import useContentUpdates from '../hooks/useContentUpdates';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { useAuth } from '../hooks/useAuth';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loading, error, isAuthenticated } = useAuth();

  // Get redirect destination from state OR query parameter
  const getRedirectUrl = () => {
    // First check location state (passed from protected routes)
    if (location.state?.from) {
      return location.state.from;
    }
    // Then check query parameter (passed from review form, etc.)
    const params = new URLSearchParams(location.search);
    const redirectParam = params.get('redirect');
    if (redirectParam) {
      return decodeURIComponent(redirectParam);
    }
    // Default to home
    return '/';
  };

  const from = getRedirectUrl();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [formError, setFormError] = useState('');

  // Handle Google Sign-In success
  const handleGoogleSuccess = () => {
    navigate(from, { replace: true });
  };

  // Handle Google Sign-In error
  const handleGoogleError = (errorMessage) => {
    setFormError(errorMessage);
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);
  useEffect(() => {
    // Handle window load event (fade out loader)
    const handleWindowLoad = () => {
      if (window.$ && window.$('.loader1').length > 0) {
        window.$(".loader1").fadeOut(800);
        window.$('.side-menu').removeClass('hidden');
      }
    };

    // If window is already loaded, trigger immediately
    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
    };
  }, []);


  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }
    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[Login] Error fetching background:', err);
    }
  }, []);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  useContentUpdates({
    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        fetchBackground();
      }
    }
  });

  return (
    <Layout>
      {/* Parallax Banner - Using banner2.jpg for login page */}
      {backgroundUrl && (
        <ParallaxBanner
          imageUrl={backgroundUrl}
          speed={0.6}
          offset={0}
          className="paralax-section-slide-data"
        />
      )}

      {/* START MAIN CONTENT - Login Form */}
      <div className="signup-form">

        {/* START MANAGE CONTENT */}
        <div className="about_content">

          <div className="container">
            <div className="row">
              <div className="col-12 col-lg-8 offset-lg-2 text-center wow slideInUp" data-wow-duration="2s">
                <h1 className="heading">Member's Area</h1>
              </div>
            </div>
          </div>

          {/* START FORM SECTION */}
          <div className="offset-lg-3 col-lg-6 col-md-12 col-sm-12 pr-lg-0 whitebox">
            <div className="widget logincontainer">

              {/* Google Sign-In Button */}
              <div className="google-auth-section mb-4">
                <GoogleSignInButton
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  buttonText="signin_with"
                  buttonId="google-signin-login"
                />
              </div>

              {/* Divider */}
              <div className="auth-divider mb-4">
                <span>or continue with email</span>
              </div>

              <form className="getin_form border-form" id="register" onSubmit={async (e) => {
                e.preventDefault();
                setFormError('');

                if (!formData.email || !formData.password) {
                  setFormError('Please fill in all fields');
                  return;
                }

                const result = await login({
                  email: formData.email,
                  password: formData.password
                });

                if (result.success) {
                  navigate(from, { replace: true });
                } else {
                  setFormError(result.error || 'Login failed');
                }
              }}>
                <div className="row">

                  {/* Error Message */}
                  {(formError || error) && (
                    <div className="col-md-12 col-sm-12">
                      <div className="alert alert-danger" role="alert">
                        {formError || error}
                      </div>
                    </div>
                  )}

                  <div className="col-md-12 col-sm-12">
                    <div className="form-group bottom35">
                      <label htmlFor="registerEmail" className="d-none"></label>
                      <input
                        className="form-control"
                        type="email"
                        placeholder="Email:"
                        required
                        id="registerEmail"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="col-md-12 col-sm-12">
                    <div className="form-group bottom35">
                      <label htmlFor="registerPass" className="d-none"></label>
                      <input
                        className="form-control"
                        type="password"
                        placeholder="Password:"
                        required
                        id="registerPass"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="col-md-12 col-sm-12">
                    <div className="form-group bottom35">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="form-check text-left">
                          <div className="custom-control custom-checkbox">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="defaultUnchecked"
                              checked={formData.rememberMe}
                              onChange={(e) => setFormData({...formData, rememberMe: e.target.checked})}
                              disabled={loading}
                            />
                            <label className="custom-control-label" htmlFor="defaultUnchecked">
                              Remember me on this device
                            </label>
                          </div>
                        </div>
                        <Link to="/forgot-password" className="forgot-password-link">
                          Forgot Password?
                        </Link>
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-12">
                    <button type="submit" className="btn pink-gradient-btn-into-black" disabled={loading}>
                      {loading ? 'Logging in...' : 'Login'}
                    </button>
                    <p className="top20 SignInclass">
                      Don't Have an Account? &nbsp;<a href="/register">Sign Up</a>
                    </p>
                  </div>

                </div>
              </form>
            </div>
          </div>
          {/* END FORM SECTION */}

        </div>
        {/* END MANAGE CONTENT */}

      </div>
      {/* END MAIN CONTENT */}
    </Layout>
  );
}

export default Login;
